import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './LoginPage.module.css';

export default function RegisterInvitePage() {
  const { login }        = useAuth();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();

  const [code,      setCode]      = useState(searchParams.get('code') || '');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [display,   setDisplay]   = useState('');
  const [codeInfo,  setCodeInfo]  = useState(null);
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);

  // Auto-check code if it came from the URL
  useEffect(() => {
    if (code) checkCode();
  }, []); // eslint-disable-line

  const checkCode = async () => {
    if (!code.trim()) return;
    try {
      const res = await fetch(`/api/auth/invite-codes/${code.trim()}`);
      const data = await res.json();
      if (res.ok) {
        setCodeInfo(data);
        setError('');
      } else {
        setCodeInfo(null);
        setError(data.detail || 'Invalid invite code.');
      }
    } catch {
      setError('Could not verify code.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeInfo) { setError('Please verify your invite code first.'); return; }
    // Was a length check only, while the message claimed a letter and a number
    // were required — so a weak password passed here and came back as a server
    // error instead. Now it checks what it says.
    if (password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    if (!/[A-Za-z]/.test(password)) {
      setError('Password must contain at least one letter.'); return;
    }
    if (!/\d/.test(password)) {
      setError('Password must contain at least one number.'); return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/register/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:        email.trim().toLowerCase(),
          password,
          legal_first:  firstName.trim(),
          legal_last:   lastName.trim(),
          display_name: display.trim() || `${firstName} ${lastName}`.trim(),
          invite_code:  code.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        login(data);
        navigate('/instructor');
      } else {
        setError(data.detail || 'Registration failed.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Was a div, so these pages had no h1 and a screen reader's heading
            list came back empty. */}
        <h1 className={styles.logo}>
          Gradiance<span className={styles.logoDot} aria-hidden="true">.</span>
        </h1>
        <p className={styles.tagline}>Create your instructor account</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="invite-code">Invite Code</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="invite-code"
                name="invite-code"
                placeholder="Paste your invite code"
                value={code}
                onChange={e => { setCode(e.target.value); setCodeInfo(null); }}
                required
              />
              <button type="button" className="btn-secondary"
                onClick={checkCode} style={{ flexShrink: 0 }}>
                Verify
              </button>
            </div>
            {codeInfo && (
              <span style={{ fontSize: 12, color: 'var(--green)' }} role="status">
                <span aria-hidden="true">✓ </span>Valid — {codeInfo.role} invite
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="invite-first">First Name</label>
            <input id="invite-first" name="given-name" autoComplete="given-name"
              placeholder="Jane" value={firstName}
              onChange={e => setFirstName(e.target.value)} required />
          </div>

          <div className={styles.field}>
            <label htmlFor="invite-last">Last Name</label>
            <input id="invite-last" name="family-name" autoComplete="family-name"
              placeholder="Doe" value={lastName}
              onChange={e => setLastName(e.target.value)} required />
          </div>

          <div className={styles.field}>
            <label htmlFor="invite-email">School Email</label>
            <input id="invite-email" name="email" type="email" autoComplete="email"
              placeholder="you@uwec.edu" value={email}
              onChange={e => setEmail(e.target.value)} required />
          </div>

          <div className={styles.field}>
            <label htmlFor="invite-display">
              Display Name{' '}
              <span style={{ color: 'var(--txt-faint)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input id="invite-display" name="nickname" autoComplete="nickname"
              placeholder={`${firstName} ${lastName}`.trim() || 'How students see your name'}
              value={display} onChange={e => setDisplay(e.target.value)} />
          </div>

          <div className={styles.field}>
            <label htmlFor="invite-password">Password</label>
            <input id="invite-password" name="new-password" type="password"
              autoComplete="new-password"
              aria-describedby="invite-password-rules"
              placeholder="At least 8 chars, 1 letter, 1 number"
              value={password} onChange={e => setPassword(e.target.value)} required />
            <span id="invite-password-rules" style={{ fontSize: 11, color: 'var(--txt-faint)' }}>
              At least 8 characters, including a letter and a number.
            </span>
          </div>

          {error && <div className={styles.error} role="alert">{error}</div>}

          <button type="submit" className={`btn-primary ${styles.submitBtn}`}
            disabled={loading || !codeInfo} aria-busy={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <div className={styles.footer}>
          <Link to="/login">Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}